import React, { useMemo, useState } from 'react';
import clsx from 'clsx';
import { withStyles } from '@material-ui/core';
import AppBar from '@material-ui/core/AppBar';
import Toolbar from '@material-ui/core/Toolbar';
import Typography from '@material-ui/core/Typography';
import SearchIcon from '@material-ui/icons/Search';
import InputBase from '@material-ui/core/InputBase';
import Dialog from '@material-ui/core/Dialog';

import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import IconButton from '@material-ui/core/IconButton';
import AccountCircle from '@material-ui/icons/AccountCircle';
import Button from '@material-ui/core/Button';
import Avatar from '@material-ui/core/Avatar';
import Tooltip from '@material-ui/core/Tooltip';
import { Link } from 'react-router-dom';
import Collapse from '@material-ui/core/Collapse';
import { Alert, AlertTitle } from '@material-ui/lab';
import Brightness4Icon from '@material-ui/icons/Brightness4';
import Brightness7Icon from '@material-ui/icons/Brightness7';
import CloseRoundedIcon from '@material-ui/icons/CloseRounded';
import MenuRoundedIcon from '@material-ui/icons/MenuRounded';

import { useAuthContext } from "../../services/AuthService";
import { useNotificationContext } from "../../services/NotificationService";
import { useThemeContext } from "../../services/ThemeService";
import AuthService from "../../services/AuthService";
import styles from './styles';

const darkModeBrandLogoSrc = `${process.env.PUBLIC_URL}/branding/pinn-hub-logo.png`;
const lightModeBrandLogoSrc = `${process.env.PUBLIC_URL}/branding/pinn-hub-logo-light.png`;

const Header = (props) => {
    const { classes } = props;

    // Get Auth Context
    const auth = useAuthContext();
    const notifications = useNotificationContext();
    const { themeName, setThemeName } = useThemeContext();
    const brandLogoSrc = themeName === 'light' ? lightModeBrandLogoSrc : darkModeBrandLogoSrc;

    // State
    const [settingsMenuAnchorEl, setSettingsMenuAnchorEl] = useState(null);
    const [navMenuAnchorEl, setNavMenuAnchorEl] = useState(null);
    const [searchDialogOpen, setSearchDialogOpen] = useState(false);

    const agentLink = useMemo(() => ({
        label: 'PINNs Agent',
        to: '/pinns-agent'
    }), []);

    const navLinks = useMemo(() => {
        const items = [
            { label: 'Published Solutions', to: '/solutions' },
            { label: 'Competitions', to: '/competitions' },
            { label: 'NeuroDiffEq Docs', to: '/docs/getting_started' },
            { label: 'Pricing', to: '/pricing' },
        ];

        if (auth.state.isAuthenticated) {
            items.splice(1, 0, { label: 'My Solutions', to: `/user/${auth.state.username}` });
        }

        return items;
    }, [auth.state.isAuthenticated, auth.state.username]);

    const errorTitles = {
        "error": "Error",
        "info": "Info",
        "warning": "Warning"
    }

    const openSettingsMenu = (event) => {
        setSettingsMenuAnchorEl(event.currentTarget);
    };
    const closeSettingsMenu = (event) => {
        setSettingsMenuAnchorEl(null);
    };
    const openNavMenu = (event) => {
        setNavMenuAnchorEl(event.currentTarget);
    };
    const closeNavMenu = () => {
        setNavMenuAnchorEl(null);
    };
    const openSearchDialog = () => {
        setSearchDialogOpen(true);
    };
    const closeSearchDialog = () => {
        setSearchDialogOpen(false);
    };
    const closeNotificationAlert = () => {
        notifications.dispatch({
            type: "HIDE_ALERT"
        })
    };

    const handleThemeToggle = () => {
        const previousTheme = themeName;
        const nextTheme = themeName === 'dark' ? 'light' : 'dark';
        setThemeName(nextTheme);

        if (auth.state.isAuthenticated) {
            AuthService.UpdateThemePreference(nextTheme)
                .then((response) => {
                    auth.dispatch({
                        type: "PROFILE",
                        payload: response.data
                    });
                })
                .catch((error) => {
                    console.log(error);
                    setThemeName(previousTheme);
                    notifications.dispatch({
                        type: "DISPLAY_ALERT",
                        payload: {
                            message: "Unable to update theme preference.",
                            severity: "error"
                        }
                    });
                });
        }
    };

    const mobileLinks = [agentLink, ...navLinks];

    return (
        <div className={classes.root}>
            <AppBar position="static" elevation={0}>
                <Toolbar variant="dense" className={classes.toolbar}>
                    <div className={classes.brandGroup}>
                        <Link to="/" className={classes.appLink}>
                            <div className={classes.appBrandLockup}>
                                <img
                                    alt="PINN Hub logo"
                                    className={classes.appLogo}
                                    src={brandLogoSrc}
                                />
                                <Typography className={classes.appTitle}>
                                    PINN Hub
                                </Typography>
                            </div>
                        </Link>
                        <Button
                            component={Link}
                            to={agentLink.to}
                            color="inherit"
                            size="small"
                            className={classes.agentLink}
                            classes={{ label: clsx(classes.navButtonLabel, classes.agentGradientLabel) }}
                        >
                            {agentLink.label}
                        </Button>
                    </div>

                    <div className={classes.toolbarActions}>
                        <div className={classes.navLinks}>
                            {navLinks.map(link => (
                                <Button
                                    key={link.label}
                                    color="inherit"
                                    component={Link}
                                    to={link.to}
                                    size="small"
                                    className={classes.navButton}
                                    classes={{ label: classes.navButtonLabel }}
                                >
                                    {link.label}
                                </Button>
                            ))}
                        </div>
                        <Tooltip title="Search">
                            <IconButton
                                onClick={openSearchDialog}
                                aria-label="open search"
                                className={classes.iconAction}
                            >
                                <SearchIcon />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title={themeName === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}>
                            <IconButton
                                onClick={handleThemeToggle}
                                aria-label="toggle theme"
                                className={classes.iconAction}
                                style={{ color: themeName === 'dark' ? '#fbbf24' : '#111827' }}
                            >
                                {themeName === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
                            </IconButton>
                        </Tooltip>
                        <div className={classes.mobileOnly}>
                            <IconButton
                                onClick={openNavMenu}
                                aria-label="open navigation menu"
                                className={classes.iconAction}
                            >
                                <MenuRoundedIcon />
                            </IconButton>
                            <Menu
                                anchorEl={navMenuAnchorEl}
                                keepMounted
                                open={Boolean(navMenuAnchorEl)}
                                onClose={closeNavMenu}
                                classes={{ paper: classes.navMenuPaper }}
                            >
                                {mobileLinks.map((link) => (
                                    <MenuItem
                                        key={link.label}
                                        component={Link}
                                        to={link.to}
                                        onClick={closeNavMenu}
                                    >
                                        {link.label}
                                    </MenuItem>
                                ))}
                                {!auth.state.isAuthenticated && (
                                    <MenuItem component={Link} to="/login" onClick={closeNavMenu}>
                                        Login/Signup
                                    </MenuItem>
                                )}
                            </Menu>
                        </div>
                        {auth.state.isAuthenticated ? (
                            <div className={classes.profileActions}>
                                <IconButton color="inherit" onClick={openSettingsMenu} className={classes.iconAction}>
                                    {auth.state.profile_image_url ?
                                        <Avatar
                                            alt={auth.state.full_name || auth.state.username || 'Profile'}
                                            src={auth.state.profile_image_url}
                                            className={classes.profileAvatar}
                                        />
                                        :
                                        <AccountCircle fontSize="small" />
                                    }
                                </IconButton>
                                <Menu
                                    anchorEl={settingsMenuAnchorEl}
                                    keepMounted
                                    open={Boolean(settingsMenuAnchorEl)}
                                    onClose={closeSettingsMenu}
                                >
                                    <MenuItem component={Link} to="/settings/apikeys" onClick={closeSettingsMenu}>API Keys</MenuItem>
                                    <MenuItem component={Link} to="/settings/profile" onClick={closeSettingsMenu}>Settings</MenuItem>
                                    <MenuItem component={Link} to="/about" onClick={closeSettingsMenu}>About Us</MenuItem>
                                    <MenuItem component={Link} to="/contact" onClick={closeSettingsMenu}>Contact Us</MenuItem>
                                    {auth.state.account_type == "admin" &&
                                        <MenuItem component={Link} to="/admin/solutionsdashboard" onClick={closeSettingsMenu}>Admin</MenuItem>
                                    }
                                    <MenuItem component={Link} to="/logout" onClick={closeSettingsMenu}>Logout</MenuItem>
                                </Menu>
                            </div>
                        ) : (
                            <div className={classes.authButtons}>
                                <Button
                                    component={Link}
                                    to="/login"
                                    variant="contained"
                                    color="primary"
                                    className={classes.actionButton}
                                >
                                    Login/Signup
                                </Button>
                            </div>
                        )}
                    </div>
                </Toolbar>
            </AppBar>
            <Dialog
                open={searchDialogOpen}
                onClose={closeSearchDialog}
                fullWidth
                maxWidth="sm"
                classes={{ paper: classes.searchDialogPaper }}
            >
                <div className={classes.searchDialogContent}>
                    <div className={classes.searchDialogField}>
                        <div className={classes.searchDialogIcon}>
                            <SearchIcon />
                        </div>
                        <InputBase
                            autoFocus
                            placeholder="Search solvers, equations, users..."
                            classes={{
                                root: classes.searchDialogInputRoot,
                                input: classes.searchDialogInput,
                            }}
                            inputProps={{ 'aria-label': 'search dialog' }}
                        />
                    </div>
                    <IconButton
                        onClick={closeSearchDialog}
                        aria-label="close search"
                        className={classes.iconAction}
                    >
                        <CloseRoundedIcon />
                    </IconButton>
                </div>
            </Dialog>
            <Collapse
                className={classes.alertContainer}
                in={notifications.state.displayAlert}
            >
                <Alert
                    onClose={closeNotificationAlert}
                    severity={notifications.state.severity}
                >
                    <AlertTitle>
                        {errorTitles[notifications.state.severity]}
                    </AlertTitle>
                    {notifications.state.message}
                </Alert>
            </Collapse>
        </div>
    );
}

export default withStyles(styles)(Header);
